// Command Palette global (Cmd+K / Ctrl+K).
// Padrão Linear/Notion/Vercel: busca universal por clientes, telas e ações.
// - Recentes: top 5 itens (localStorage), aparece quando input vazio.
// - Clientes: busca async no Supabase por razao_social / nome_fantasia / cnpj.
// - Telas: ~50 rotas estáticas do CRM (matching local instantâneo do cmdk).
// - Ações: atalhos pra fluxos comuns (novo cliente, abrir WhatsApp Inbox, etc).

import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  LayoutDashboard,
  Briefcase,
  Settings,
  Users,
  KanbanSquare,
  MessageSquare,
  TrendingUp,
  Trophy,
  FileSpreadsheet,
  Bell,
  ListChecks,
  Award,
  PhoneCall,
  Search,
  Clock,
  ArrowRight,
  Globe,
  ShieldCheck,
  HeadphonesIcon,
  Layers,
  Plus,
  Calendar,
  UserCog,
  Mail,
  Network,
  Compass,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useCommandPalette } from "@/hooks/use-command-palette";
import { useDebounce } from "@/hooks/use-debounce";
import { supabase } from "@/integrations/supabase/client";

type ScreenItem = {
  id: string;
  label: string;
  path: string;
  group: "Dashboards" | "Comercial" | "Operação" | "Gestão" | "Configurações" | "Meu";
  Icon: typeof LayoutDashboard;
  keywords?: string[];
};

const SCREENS: ScreenItem[] = [
  // Dashboards
  { id: "dash-gerencial", label: "Dashboard Gerencial", path: "/dashboards?tab=gerencial", group: "Dashboards", Icon: LayoutDashboard, keywords: ["dashboard", "gerencial", "indicadores"] },
  { id: "dash-venda", label: "Venda Diária", path: "/dashboards?tab=venda-diaria", group: "Dashboards", Icon: TrendingUp, keywords: ["venda", "dia", "vendas"] },
  { id: "dash-slas", label: "SLAs", path: "/dashboards?tab=slas", group: "Dashboards", Icon: ShieldCheck, keywords: ["sla", "prazo", "acompanhamento"] },
  { id: "dash-visao-prime", label: "Visão Prime", path: "/dashboards?tab=visao-prime", group: "Dashboards", Icon: Compass, keywords: ["visao", "prime", "produtos"] },
  { id: "dash-cancelamentos", label: "Dashboard Cancelamentos", path: "/dashboards?tab=cancelado", group: "Dashboards", Icon: TrendingUp, keywords: ["cancelamento", "churn"] },
  { id: "hora-a-hora", label: "Hora a Hora", path: "/dashboards?tab=hora-a-hora", group: "Dashboards", Icon: Clock, keywords: ["hora", "atividade"] },

  // Comercial
  { id: "comercial", label: "Comercial", path: "/comercial", group: "Comercial", Icon: Briefcase, keywords: ["comercial", "vendas"] },
  { id: "carteira", label: "Minha Carteira", path: "/comercial?tab=carteira", group: "Comercial", Icon: Building2, keywords: ["carteira", "clientes"] },
  { id: "meus-leads", label: "Meus Leads", path: "/comercial?tab=prospeccao", group: "Comercial", Icon: Users, keywords: ["lead", "prospect"] },
  { id: "faturas", label: "Faturas (consultor)", path: "/comercial?tab=faturas", group: "Comercial", Icon: FileSpreadsheet, keywords: ["fatura", "boleto", "consultor"] },
  { id: "visao-comissoes", label: "Minha Produtividade", path: "/visao-comissoes", group: "Comercial", Icon: Award, keywords: ["comissao", "ganho", "produtividade"] },

  // Operação
  { id: "whatsapp-inbox", label: "WhatsApp Inbox", path: "/whatsapp-inbox", group: "Operação", Icon: MessageSquare, keywords: ["whatsapp", "atendimento", "chat", "wpp"] },
  { id: "discadora", label: "Discadora", path: "/discadora", group: "Operação", Icon: PhoneCall, keywords: ["discadora", "ligacao", "telefone"] },
  { id: "planilha", label: "Planilha", path: "/planilha", group: "Operação", Icon: FileSpreadsheet, keywords: ["planilha", "tabela"] },
  { id: "kanbans", label: "Kanban", path: "/kanbans", group: "Operação", Icon: KanbanSquare, keywords: ["kanban", "quadro"] },
  { id: "produtos", label: "Produtos", path: "/configuracoes?tab=produtos", group: "Operação", Icon: Layers, keywords: ["produto", "catalogo"] },
  { id: "cobertura", label: "Cobertura", path: "/cobertura", group: "Operação", Icon: Globe, keywords: ["cobertura", "cep", "claro", "banda larga"] },
  { id: "mailing-diarios", label: "Mailing", path: "/mailing-diarios", group: "Operação", Icon: Mail, keywords: ["mailing", "lista"] },
  { id: "email-marketing", label: "E-mail Marketing", path: "/email-marketing", group: "Operação", Icon: Mail, keywords: ["email", "marketing", "campanha"] },
  { id: "fluxo-crm", label: "Fluxo CRM", path: "/fluxo-crm", group: "Operação", Icon: Network, keywords: ["fluxo", "mapa", "crm"] },

  // Gestão
  { id: "produtividade", label: "Produtividade", path: "/gestao?tab=produtividade", group: "Gestão", Icon: TrendingUp, keywords: ["produtividade"] },
  { id: "usuarios", label: "Usuários", path: "/usuarios", group: "Gestão", Icon: UserCog, keywords: ["usuario", "user"] },
  { id: "equipes", label: "Equipes", path: "/equipes", group: "Gestão", Icon: Users, keywords: ["equipe", "time"] },
  { id: "regras-comissao", label: "Regras de Produtividade", path: "/regras-comissao", group: "Gestão", Icon: Award, keywords: ["regra", "comissao", "produtividade"] },
  { id: "gestao-avisos", label: "Gestão de Avisos", path: "/avisos", group: "Gestão", Icon: Bell, keywords: ["aviso", "comunicado"] },
  { id: "painel-avisos", label: "Painel de Avisos", path: "/painel-avisos", group: "Gestão", Icon: Bell, keywords: ["aviso", "painel"] },

  // Configurações
  { id: "configuracoes", label: "Configurações", path: "/configuracoes", group: "Configurações", Icon: Settings, keywords: ["config", "ajustes"] },
  { id: "config-obrigatorios", label: "Campos Obrigatórios", path: "/configuracoes?tab=obrigatorios", group: "Configurações", Icon: ListChecks, keywords: ["obrigatorio", "validacao"] },
  { id: "config-cnpj", label: "Token CNPJ", path: "/configuracoes?tab=cnpj", group: "Configurações", Icon: Building2, keywords: ["cnpj", "token"] },
  { id: "config-status-pedidos", label: "Status de Pedidos", path: "/configuracoes?tab=status-pedidos", group: "Configurações", Icon: ListChecks, keywords: ["status", "pedido"] },
  { id: "config-motivos-cancelamento", label: "Motivos de Cancelamento", path: "/configuracoes?tab=motivos-cancelamento", group: "Configurações", Icon: ListChecks, keywords: ["motivo", "cancelamento"] },

  // Meu
  { id: "meu-perfil", label: "Meu Perfil", path: "/meu-perfil", group: "Meu", Icon: UserCog, keywords: ["perfil", "conta"] },
  { id: "minhas-solicitacoes", label: "Solicitações e aprovações", path: "/busca-lead-360?solicitacoes=minhas", group: "Meu", Icon: ListChecks, keywords: ["solicitacao", "pedido"] },
  { id: "busca-lead-360", label: "Busca 360", path: "/busca-lead-360", group: "Meu", Icon: Search, keywords: ["busca", "consulta", "cliente", "lead", "360", "telefone", "cnpj"] },
];

type RecentEntry = { id: string; type: "screen" | "cliente"; label: string; path: string; ts: number };
const RECENT_KEY = "cmdk-recent";
const RECENT_MAX = 5;

function loadRecents(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(entry: Omit<RecentEntry, "ts">) {
  try {
    const cur = loadRecents().filter((r) => r.id !== entry.id);
    const next = [{ ...entry, ts: Date.now() }, ...cur].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

type ClienteRow = {
  id: string;
  razao_social: string | null;
  nome_fantasia: string | null;
  cnpj: string | null;
};

async function searchClientes(q: string): Promise<ClienteRow[]> {
  const term = q.trim();
  if (term.length < 2) return [];
  // Busca por razao_social OU nome_fantasia OU cnpj (digitos puros).
  const digits = term.replace(/\D/g, "");
  const orParts = [
    `razao_social.ilike.%${term}%`,
    `nome_fantasia.ilike.%${term}%`,
  ];
  if (digits.length >= 3) orParts.push(`cnpj.ilike.%${digits}%`);
  const { data, error } = await supabase
    .from("clientes")
    .select("id, razao_social, nome_fantasia, cnpj")
    .or(orParts.join(","))
    .eq("ativo", true)
    .limit(10);
  if (error) {
    console.error("[CommandPalette] busca clientes:", error);
    return [];
  }
  return (data ?? []) as ClienteRow[];
}

function formatCnpj(cnpj: string | null): string {
  if (!cnpj) return "";
  const d = cnpj.replace(/\D/g, "");
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 200);
  const [recents, setRecents] = useState<RecentEntry[]>([]);

  // Recarrega recentes toda vez que abre
  useEffect(() => {
    if (open) {
      setRecents(loadRecents());
      setSearch("");
    }
  }, [open]);

  const { data: clientes = [], isFetching } = useQuery({
    queryKey: ["cmdk-clientes", debounced],
    queryFn: () => searchClientes(debounced),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const screensByGroup = useMemo(() => {
    const groups: Record<string, ScreenItem[]> = {};
    for (const s of SCREENS) {
      (groups[s.group] ??= []).push(s);
    }
    return groups;
  }, []);

  const handleSelect = useCallback(
    (entry: Omit<RecentEntry, "ts">) => {
      pushRecent(entry);
      setOpen(false);
      navigate(entry.path);
    },
    [navigate, setOpen],
  );

  const showRecents = !search.trim() && recents.length > 0;
  const hasQuery = debounced.trim().length >= 2;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Buscar clientes, telas, ações…"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>
          {hasQuery && isFetching
            ? "Buscando…"
            : "Nada encontrado. Tente outro termo."}
        </CommandEmpty>

        {showRecents && (
          <>
            <CommandGroup heading="Recentes">
              {recents.map((r) => (
                <CommandItem
                  key={`recent-${r.id}`}
                  value={`recent ${r.label}`}
                  onSelect={() => handleSelect({ id: r.id, type: r.type, label: r.label, path: r.path })}
                >
                  <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{r.label}</span>
                  <ArrowRight className="ml-2 h-3.5 w-3.5 text-muted-foreground" />
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {hasQuery && (
          <CommandGroup heading={isFetching ? "Clientes (buscando…)" : "Clientes"}>
            {clientes.map((c) => {
              const label = c.razao_social || c.nome_fantasia || formatCnpj(c.cnpj) || "Cliente sem nome";
              const sub = [c.nome_fantasia && c.razao_social && c.nome_fantasia, formatCnpj(c.cnpj)]
                .filter(Boolean)
                .join(" · ");
              const path = `/comercial?tab=carteira&cliente=${c.id}`;
              return (
                <CommandItem
                  key={`cli-${c.id}`}
                  value={`cliente ${label} ${c.cnpj ?? ""} ${c.nome_fantasia ?? ""}`}
                  onSelect={() => handleSelect({ id: c.id, type: "cliente", label, path })}
                >
                  <Building2 className="mr-2 h-4 w-4 text-primary" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate text-sm">{label}</span>
                    {sub && <span className="truncate text-[11px] text-muted-foreground">{sub}</span>}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {Object.entries(screensByGroup).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map((s) => {
              const Icon = s.Icon;
              const kw = (s.keywords ?? []).join(" ");
              return (
                <CommandItem
                  key={s.id}
                  value={`${s.label} ${kw}`}
                  onSelect={() =>
                    handleSelect({ id: s.id, type: "screen", label: s.label, path: s.path })
                  }
                >
                  <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{s.label}</span>
                  <CommandShortcut>{group}</CommandShortcut>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}

        <CommandSeparator />
        <CommandGroup heading="Ações">
          <CommandItem
            value="acao novo cliente cadastrar empresa"
            onSelect={() =>
              handleSelect({ id: "act-novo-cliente", type: "screen", label: "Novo cliente", path: "/comercial?tab=carteira&novo=1" })
            }
          >
            <Plus className="mr-2 h-4 w-4 text-primary" />
            <span className="flex-1">Novo cliente</span>
          </CommandItem>
          <CommandItem
            value="acao abrir whatsapp inbox atendimento"
            onSelect={() =>
              handleSelect({ id: "act-whatsapp", type: "screen", label: "Abrir WhatsApp Inbox", path: "/whatsapp-inbox" })
            }
          >
            <MessageSquare className="mr-2 h-4 w-4 text-success" />
            <span className="flex-1">Abrir WhatsApp Inbox</span>
          </CommandItem>
          <CommandItem
            value="acao abrir discadora telefone"
            onSelect={() =>
              handleSelect({ id: "act-discadora", type: "screen", label: "Abrir Discadora", path: "/discadora" })
            }
          >
            <PhoneCall className="mr-2 h-4 w-4 text-primary" />
            <span className="flex-1">Abrir Discadora</span>
          </CommandItem>
          <CommandItem
            value="acao abrir agenda compromissos"
            onSelect={() =>
              handleSelect({ id: "act-agenda", type: "screen", label: "Abrir Agenda", path: "/comercial?tab=agenda" })
            }
          >
            <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
            <span className="flex-1">Abrir Agenda</span>
          </CommandItem>
          <CommandItem
            value="acao configuracoes ajustes"
            onSelect={() =>
              handleSelect({ id: "act-config", type: "screen", label: "Configurações", path: "/configuracoes" })
            }
          >
            <Settings className="mr-2 h-4 w-4 text-muted-foreground" />
            <span className="flex-1">Ir para Configurações</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
